import React, { useEffect, useState } from 'react';
import { MessageCircle, MessageSquare, UserPen } from 'lucide-react';

const Card = ({ className = '', children, ...rest }) => <section className={`glass rounded-[28px] ${className}`} {...rest}>{children}</section>;
const Label = ({ children }) => <p className="label-text text-[var(--text-muted)]">{children}</p>;

const mmss = (sec) => {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`;
};

const PHASE = { ready: 'przygotowuje się', work: 'seria w trakcie', rest: 'przerwa', summary: 'kończy trening' };

// "Teraz trenuje": clients currently in the workout player. Elapsed time ticks
// locally between the dashboard's polls (the server value is from the last
// heartbeat).
export const LiveNowCard = ({ items }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!items.length) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [items.length]);
  if (!items.length) return null;
  return (
    <Card className="p-5 sm:p-6 flex flex-col gap-3" data-testid="live-now">
      <div className="flex items-center gap-2.5">
        <span className="relative flex w-2.5 h-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-60" />
          <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-[#22c55e]" />
        </span>
        <Label>Teraz trenuje · {items.length}</Label>
      </div>
      {items.map((w) => {
        const sinceSeen = (Date.now() - new Date(w.last_seen).getTime()) / 1000;
        const elapsed = w.paused ? w.elapsed_seconds : w.elapsed_seconds + sinceSeen;
        return (
          <div key={w.client_id} className="glass-soft rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{w.client_name}</p>
              <p className="text-xs text-[var(--text-muted)] truncate">
                {w.phase === 'summary' ? 'Kończy trening' : (
                  <>Ćwiczenie {w.exercise_index + 1}/{w.exercises_total}: {w.exercise_name} · seria {w.set_index + 1}/{w.sets_total}</>
                )}
              </p>
              <p className="text-[11px] text-[var(--text-muted)]">
                {w.paused ? 'pauza' : PHASE[w.phase]}{w.plan_name ? ` · ${w.plan_name}, dzień ${w.day_number}` : ''}
              </p>
            </div>
            <span className="big-num text-xl tabular-nums shrink-0">{mmss(elapsed)}</span>
          </div>
        );
      })}
    </Card>
  );
};

export const phoneDigits = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  if (!d) return null;
  return d.length === 9 ? `48${d}` : d; // Polish numbers saved without the country code
};

const lastLabel = (days) => {
  if (days == null) return 'brak treningów w aplikacji';
  if (days === 0) return 'ostatni dziś';
  if (days === 1) return 'ostatni wczoraj';
  return `ostatni ${days} dni temu`;
};

const message = (it, trainerName) => {
  const first = it.client_name.split(' ')[0];
  return `Cześć ${first}! W tym tygodniu w aplikacji widzę ${it.done_this_week} z ${it.frequency} treningów z planu. `
    + 'Wszystko w porządku? Daj znać, czy coś przeszkadza - dopasujemy plan.'
    + (trainerName ? ` ${trainerName.split(' ')[0]}` : '');
};

// Clients behind their plan this week, with one-tap WhatsApp / SMS asking
// how it's going (opens the trainer's own app with the text filled in).
export const BehindCard = ({ items, trainerName, onOpen }) => (
  <Card className="p-6 flex flex-col gap-3" data-testid="behind-plan">
    <div className="flex justify-between items-center">
      <h2 className="text-lg font-extrabold tracking-tight">Zaległe treningi</h2>
      <span className="chip">{items.length}</span>
    </div>
    {items.length === 0 && <p className="text-sm text-[var(--text-muted)]">Wszyscy trzymają się planu w tym tygodniu.</p>}
    {items.map((it) => {
      const phone = phoneDigits(it.phone);
      const text = encodeURIComponent(message(it, trainerName));
      return (
        <div key={it.client_id} className="glass-soft rounded-2xl px-4 py-3 flex flex-col gap-2.5">
          <button type="button" onClick={() => onOpen(it.client_id)} className="text-left">
            <p className="text-sm font-bold">{it.client_name}</p>
            <p className="text-xs text-[var(--text-muted)]">
              {it.done_this_week} z {it.frequency} w tym tygodniu (do dziś {it.expected_by_today})
              {' · '}{lastLabel(it.days_since_last)}
            </p>
          </button>
          {phone ? (
            <div className="flex gap-2">
              <a href={`https://wa.me/${phone}?text=${text}`} target="_blank" rel="noopener noreferrer"
                className="cta-ghost h-9 px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5" data-testid="ask-whatsapp">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
              <a href={`sms:+${phone}?&body=${text}`}
                className="cta-ghost h-9 px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5" data-testid="ask-sms">
                <MessageSquare className="w-3.5 h-3.5" /> SMS
              </a>
            </div>
          ) : (
            <button type="button" onClick={() => onOpen(it.client_id)}
              className="cta-ghost h-9 px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5 self-start">
              <UserPen className="w-3.5 h-3.5" /> Dodaj telefon, żeby zapytać
            </button>
          )}
        </div>
      );
    })}
  </Card>
);

const STATE_LABEL = { early: 'otwarty od soboty', due: 'termin dziś', overdue: 'zaległe (do wtorku)', closed: 'zamknięty' };

// Weekly reports this week: who sent it (to comment) and who hasn't (with a
// one-tap WhatsApp / SMS reminder).
export const ReportsCard = ({ status, onOpen }) => {
  if (!status || status.submitted.length + status.missing.length === 0) return null;
  const d = new Date(`${status.week_start}T00:00:00`);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 6);
  const f = (x) => `${x.getDate()}.${String(x.getMonth() + 1).padStart(2, '0')}`;
  const reminder = (name) => encodeURIComponent(
    `Cześć ${name.split(' ')[0]}! Przypominam o raporcie tygodniowym w aplikacji HStrener OS (${f(d)}–${f(end)}) - zajmie ok. 3 minuty. Dzięki!`);
  return (
    <Card className="p-6 flex flex-col gap-3" data-testid="reports-status">
      <div className="flex justify-between items-center gap-3">
        <h2 className="text-lg font-extrabold tracking-tight">Raporty tygodniowe</h2>
        <span className="chip">{status.submitted.length}/{status.submitted.length + status.missing.length}</span>
      </div>
      <p className="text-xs text-[var(--text-muted)]">Tydzień {f(d)}–{f(end)} · {STATE_LABEL[status.state]}</p>
      {status.submitted.map((r) => (
        <button key={r.client_id} type="button" onClick={() => onOpen(r.client_id)}
          className="glass-soft rounded-2xl px-4 py-2.5 flex items-center justify-between gap-3 text-left">
          <span className="text-sm font-bold truncate">{r.client_name}</span>
          <span className={`chip text-[11px] ${r.commented ? '' : 'border-[#C9A84C]/50'}`}>{r.commented ? 'skomentowany' : 'do komentarza'}</span>
        </button>
      ))}
      {status.missing.length > 0 && <p className="label-text text-[var(--text-muted)] text-[0.6rem] mt-1">Brak raportu</p>}
      {status.missing.map((m) => {
        const phone = phoneDigits(m.phone);
        return (
          <div key={m.client_id} className="glass-soft rounded-2xl px-4 py-2.5 flex items-center justify-between gap-2">
            <button type="button" onClick={() => onOpen(m.client_id)} className="text-sm font-bold truncate text-left">
              {m.client_name}{!m.has_login && <span className="block text-[11px] font-normal text-[var(--text-muted)]">nie ma jeszcze konta</span>}
            </button>
            {phone ? (
              <span className="flex gap-1.5 shrink-0">
                <a href={`https://wa.me/${phone}?text=${reminder(m.client_name)}`} target="_blank" rel="noopener noreferrer"
                  aria-label={`Przypomnij ${m.client_name} na WhatsApp`} className="cta-ghost w-9 h-9 rounded-full flex items-center justify-center"><MessageCircle className="w-4 h-4" /></a>
                <a href={`sms:+${phone}?&body=${reminder(m.client_name)}`} aria-label={`Przypomnij ${m.client_name} SMS-em`}
                  className="cta-ghost w-9 h-9 rounded-full flex items-center justify-center"><MessageSquare className="w-4 h-4" /></a>
              </span>
            ) : <span className="text-[11px] text-[var(--text-muted)] shrink-0">brak telefonu</span>}
          </div>
        );
      })}
    </Card>
  );
};
